import { SUPABASE_URL, SUPABASE_ANON_KEY } from '../config/env';

const supabaseRequest = async (endpoint, options = {}) => {
  const response = await fetch(`${SUPABASE_URL}${endpoint}`, {
    ...options,
    headers: {
      'apikey': SUPABASE_ANON_KEY,
      'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
      'Content-Type': 'application/json',
      ...options.headers,
    },
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Request failed' }));
    throw new Error(error.message || 'Request failed');
  }
  // Handle empty responses (204 No Content) for PATCH/DELETE requests
  const contentType = response.headers.get('content-type');
  if (!contentType || !contentType.includes('application/json')) {
    return null; // Empty response
  }
  const text = await response.text();
  if (!text) {
    return null; // Empty response
  }
  try {
    return JSON.parse(text);
  } catch (e) {
    return null; // Invalid JSON, return null
  }
};

const isUuid = (value) => /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(String(value || ''));

const chunkArray = (arr, size) => {
  const out = [];
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
  return out;
};

const enrichMechanicsWithTiming = async (mechanics) => {
  const list = Array.isArray(mechanics) ? mechanics : [];
  if (list.length === 0) return list;

  // If timing already exists for most rows, skip extra calls.
  const anyMissingTiming = list.some((m) => isUuid(m?.id) && !(m?.timing || m?.timings));
  if (!anyMissingTiming) return list;

  const ids = Array.from(
    new Set(
      list
        .map((m) => m?.id)
        .filter((id) => isUuid(id))
    )
  );
  if (ids.length === 0) return list;

  const idToTiming = new Map();
  try {
    const batches = chunkArray(ids, 200);
    for (const batch of batches) {
      const endpoint = `/rest/v1/mechanics?select=id,timing&id=in.(${batch.join(',')})`;
      const rows = await supabaseRequest(endpoint, { method: 'GET' });
      (rows || []).forEach((r) => {
        if (!r?.id) return;
        idToTiming.set(r.id, r.timing || '');
      });
    }
  } catch (e) {
    console.warn('[ServiceAdvisorService] Failed to enrich mechanic timings:', e);
    return list;
  }

  return list.map((m) => {
    const id = m?.id;
    if (!isUuid(id)) return m;
    if (m?.timing || m?.timings) return m;
    const timing = idToTiming.get(id);
    if (!timing) return m;
    return { ...m, timing };
  });
};

const enrichSearchResultsWithTiming = async (results) => {
  const list = Array.isArray(results) ? results : [];
  if (list.length === 0) return list;

  const candidates = list
    .map((r) => r?.mechanic_data || r)
    .filter(Boolean);

  const ids = Array.from(
    new Set(
      candidates
        .map((m) => m?.id)
        .filter((id) => isUuid(id))
    )
  );

  if (ids.length === 0) return list;

  const idToTiming = new Map();
  try {
    const batches = chunkArray(ids, 200);
    for (const batch of batches) {
      const endpoint = `/rest/v1/mechanics?select=id,timing&id=in.(${batch.join(',')})`;
      const rows = await supabaseRequest(endpoint, { method: 'GET' });
      (rows || []).forEach((r) => {
        if (!r?.id) return;
        idToTiming.set(r.id, r.timing || '');
      });
    }
  } catch (e) {
    console.warn('[ServiceAdvisorService] Failed to enrich search mechanic timings:', e);
    return list;
  }

  return list.map((r) => {
    if (r?.mechanic_data && isUuid(r.mechanic_data?.id) && !(r.mechanic_data?.timing || r.mechanic_data?.timings)) {
      const timing = idToTiming.get(r.mechanic_data.id);
      if (timing) return { ...r, mechanic_data: { ...r.mechanic_data, timing } };
      return r;
    }
    if (isUuid(r?.id) && !(r?.timing || r?.timings)) {
      const timing = idToTiming.get(r.id);
      if (timing) return { ...r, timing };
      return r;
    }
    return r;
  });
};

export class ServiceAdvisorService {
  static async getComplaints(advisorId) {
    try {
      const data = await supabaseRequest(`/rest/v1/rpc/service_advisor_get_complaints`, {
        method: 'POST',
        body: JSON.stringify({ p_advisor_id: advisorId }),
      });
      return { success: true, data: data || [], error: null };
    } catch (error) {
      console.error('Error fetching complaints:', error);
      return { success: false, data: [], error: error.message };
    }
  }

  static async getComplaintsFull(advisorId, status = '', showAll = true) {
    try {
      const data = await supabaseRequest(`/rest/v1/rpc/service_advisor_get_complaints_full`, {
        method: 'POST',
        body: JSON.stringify({ 
          p_advisor_id: advisorId || null,
          p_status: status || '',
          p_show_all: showAll
        }),
      });
      return { success: true, data: data || [], error: null };
    } catch (error) {
      console.error('Error fetching full complaints:', error);
      return { success: false, data: [], error: error.message };
    }
  }

  static async getRegisteredComplaints(advisorId) {
    try {
      const data = await supabaseRequest(`/rest/v1/rpc/service_advisor_get_registered_complaints`, {
        method: 'POST',
        body: JSON.stringify({ p_advisor_id: advisorId }),
      });
      return { success: true, data: data || [], error: null };
    } catch (error) {
      console.error('Error fetching registered complaints:', error);
      return { success: false, data: [], error: error.message };
    }
  }

  // Get available mechanics - EXACTLY like mobile app
  static async getAvailableMechanics(serviceType = null, location = null, pincode = null) {
    try {
      const data = await supabaseRequest(`/rest/v1/rpc/get_available_mechanics`, {
        method: 'POST',
        body: JSON.stringify({
          p_service_type: serviceType || null,
          p_location: location || null,
          p_pincode: pincode || null,
        }),
      });
      const enriched = await enrichMechanicsWithTiming(data || []);
      return { success: true, data: enriched || [], error: null };
    } catch (error) {
      console.error('Error fetching available mechanics:', error);
      return { success: false, data: [], error: error.message };
    }
  }

  // Backward compatibility wrapper
  static async getMechanics(searchParams = {}) {
    // Use getAvailableMechanics with proper parameters
    return await this.getAvailableMechanics(
      searchParams.serviceType || searchParams.service_tag || null,
      searchParams.location || null,
      searchParams.pincode || null
    );
  }

  // Search mechanics (unregistered) - for search tab
  static async searchMechanics(query, serviceType = null) {
    try {
      // Try searching by pincode first if query looks like a pincode
      const isPincode = /^\d{6}$/.test(query.trim());
      
      if (isPincode) {
        // Search by pincode
        const data = await supabaseRequest(`/rest/v1/rpc/search_mechanics_by_pincode`, {
          method: 'POST',
          body: JSON.stringify({
            p_pincode: query.trim(),
            p_service_type: serviceType || null,
            p_limit: 50
          }),
        });
        const enriched = await enrichSearchResultsWithTiming(data || []);
        return { success: true, data: enriched || [], error: null };
      } else {
        // Search by text (name, phone, service tag, area)
        const data = await supabaseRequest(`/rest/v1/rpc/search_mechanics_by_text`, {
          method: 'POST',
          body: JSON.stringify({
            p_query: query.trim(),
            p_service_type: serviceType || null,
            p_limit: 50
          }),
        });
        const enriched = await enrichSearchResultsWithTiming(data || []);
        return { success: true, data: enriched || [], error: null };
      }
    } catch (error) {
      console.error('Error searching mechanics:', error);
      return { success: false, data: [], error: error.message };
    }
  }

  static async getMechanicById(mechanicId) {
    try {
      if (!isUuid(mechanicId)) return { success: true, data: null, error: null };
      const rows = await supabaseRequest(`/rest/v1/mechanics?id=eq.${mechanicId}&select=id,mechanic_name,number,timing`, { method: 'GET' });
      const row = Array.isArray(rows) ? (rows[0] || null) : rows;
      return { success: true, data: row || null, error: null };
    } catch (error) {
      console.error('Error fetching mechanic by id:', error);
      return { success: false, data: null, error: error.message };
    }
  }

  // Add mechanic (reuse existing RPC used in mobile-style flow)
  // NOTE: This expects the RPC `admin_insert_mechanic_simple` to exist in DB.
  // Web app uses anon key, so DB must grant EXECUTE to anon for this RPC.
  static async addMechanicSimple(payload = {}) {
    try {
      const {
        code,
        mechanic_name,
        number,
        alternate_number1,
        alternate_number2,
        service_tag,
        service_tag2,
        service_tag3,
        timing,
        service_areas,
        // backward-compat single area
        area,
        pincode,
        state,
      } = payload || {};

      const areas = Array.isArray(service_areas) ? service_areas : [];
      const normalizedAreas = areas.length
        ? areas
        : (area || pincode || state)
          ? [{ area: area || '', pincode: pincode || '', state: state || '' }]
          : [];

      const areaParams = {};
      for (let i = 0; i < 20; i += 1) {
        const row = normalizedAreas[i] || {};
        const a = String(row?.area || '').trim();
        const p = String(row?.pincode || '').trim();
        const s = String(row?.state || '').trim();
        // RPC includes area only if all 3 are provided
        areaParams[`p_area${i + 1}`] = a || null;
        areaParams[`p_pincode${i + 1}`] = p || null;
        areaParams[`p_state${i + 1}`] = s || null;
      }

      const data = await supabaseRequest(`/rest/v1/rpc/admin_insert_mechanic_simple`, {
        method: 'POST',
        body: JSON.stringify({
          p_code: code,
          p_mechanic_name: mechanic_name,
          p_number: number,
          p_alternate_number1: alternate_number1 || null,
          p_alternate_number2: alternate_number2 || null,
          p_service_tag: service_tag || null,
          p_service_tag2: service_tag2 || null,
          p_service_tag3: service_tag3 || null,
          p_timing: timing || null,
          p_active: true,
          ...areaParams,
        }),
      });

      if (data?.success === false) {
        return { success: false, data: null, error: data?.error || 'Failed to add mechanic' };
      }
      return { success: true, data: data || null, error: null };
    } catch (error) {
      return { success: false, data: null, error: error?.message || String(error) };
    }
  }

  // Assign mechanic to service request - EXACTLY like mobile app
  static async assignMechanicToRequest(
    requestId,
    mechanicId,
    mechanicName,
    mechanicContact,
    dispatchRemark = null,
    mechanicPayment = null,
    assignmentRemark = null
  ) {
    try {
      const body = {
        p_request_id: requestId,
        p_mechanic_id: mechanicId,
        p_mechanic_name: mechanicName,
        p_mechanic_contact: mechanicContact,
        p_dispatch_remark: dispatchRemark || null,
        p_mechanic_payment: mechanicPayment || null,
        p_assignment_remark: assignmentRemark || null,
      };
      const data = await supabaseRequest(`/rest/v1/rpc/assign_mechanic_to_request`, {
        method: 'POST',
        body: JSON.stringify(body),
      });
      const result = Array.isArray(data) ? data[0] : data;
      return {
        success: result?.success || false,
        message: result?.message || 'Mechanic assigned successfully',
        error: null
      };
    } catch (error) {
      console.error('Error assigning mechanic:', error);
      return { success: false, error: error.message, message: error.message };
    }
  }

  // Backward compatibility wrapper
  static async assignMechanic(requestId, mechanicId, options = {}) {
    // Get mechanic details first
    try {
      const mechanicsResult = await this.getMechanics({});
      const mechanic = mechanicsResult.data?.find((m) => m.id === mechanicId);
      
      if (!mechanic) {
        return { success: false, error: 'Mechanic not found' };
      }

      return await this.assignMechanicToRequest(
        requestId,
        mechanicId,
        mechanic.name || 'Unknown',
        mechanic.contact_number || mechanic.phone || '',
        options.dispatchRemark || null,
        options.payment || null,
        options.remark || null
      );
    } catch (error) {
      console.error('Error in assignMechanic:', error);
      return { success: false, error: error.message };
    }
  }

  // Get complaint by ID - EXACTLY like mobile app (tries v2 RPC first, then direct query)
  static async getComplaintById(complaintId) {
    try {
      console.log('=== FETCHING COMPLAINT BY ID (WEB) ===');
      console.log('Complaint ID:', complaintId);
      
      // Validate input
      if (!complaintId || typeof complaintId !== 'string') {
        console.error('Invalid complaint ID:', complaintId);
        return { success: false, data: null, error: 'Invalid complaint ID' };
      }
      
      // Try the enhanced v2 function first, fallback to direct query (exactly like mobile app)
      let data, error;
      try {
        const v2Response = await fetch(`${SUPABASE_URL}/rest/v1/rpc/get_complaint_by_id_v2`, {
          method: 'POST',
          headers: {
            'apikey': SUPABASE_ANON_KEY,
            'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ complaint_id: complaintId }),
        });
        
        if (!v2Response.ok) {
          throw new Error('V2 function not available');
        }
        
        const v2Data = await v2Response.json();
        data = v2Data;
        error = null;
        console.log('✅ Fetched via get_complaint_by_id_v2 RPC');
      } catch (v2Error) {
        console.log('⚠️ V2 RPC not available, falling back to direct query');
        // Fallback to direct query
        const directResponse = await fetch(`${SUPABASE_URL}/rest/v1/service_requests?id=eq.${complaintId}&select=*`, {
          method: 'GET',
          headers: {
            'apikey': SUPABASE_ANON_KEY,
            'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
            'Content-Type': 'application/json',
          },
        });
        
        if (!directResponse.ok) {
          throw new Error('Failed to fetch complaint');
        }
        
        const directData = await directResponse.json();
        data = Array.isArray(directData) ? (directData[0] || null) : directData;
        error = null;
        console.log('✅ Fetched via direct query');
      }
      
      // Parse the JSON response with better error handling (if RPC returned JSON string)
      let result;
      try {
        if (typeof data === 'string') {
          result = JSON.parse(data);
        } else if (typeof data === 'object' && data !== null) {
          result = data;
        } else {
          console.warn('Unexpected data type:', typeof data);
          return { success: false, data: null, error: 'Invalid response format' };
        }
      } catch (parseError) {
        console.error('Failed to parse response:', parseError);
        return { success: false, data: null, error: 'Failed to parse response' };
      }
      
      // Handle RPC response format (may have success/data wrapper)
      const complaint = result?.data || result;
      
      if (!complaint || !complaint.id) {
        console.warn('Invalid complaint data received');
        return { success: false, data: null, error: 'Complaint not found' };
      }
      
      console.log('✅ Complaint fetched successfully:', complaint.id);
      return { success: true, data: complaint, error: null };
    } catch (error) {
      console.error('Error fetching complaint detail:', error);
      return { success: false, data: null, error: error.message || 'Failed to fetch complaint' };
    }
  }

  // Get complaint timeline - EXACTLY like mobile app
  static async getComplaintTimeline(complaintId) {
    try {
      // Use the same RPC as mobile app: service_request_get_timeline
      const data = await supabaseRequest(`/rest/v1/rpc/service_request_get_timeline`, {
        method: 'POST',
        body: JSON.stringify({ p_request_id: complaintId }),
      });
      
      // Handle array or single object response
      const timeline = Array.isArray(data) ? data : (data ? [data] : []);
      return { success: true, data: timeline, error: null };
    } catch (error) {
      console.error('Error fetching complaint timeline:', error);
      return { success: false, data: [], error: error.message };
    }
  }

  // Update complaint status - EXACTLY like mobile app (uses service_request_update_status_v2 or service_request_update_status)
  static async updateComplaintStatus(complaintId, newStatus, notes, actorId = null) {
    try {
      // Try v2 first, fallback to original (exactly like mobile app)
      let data, error;
      try {
        const v2Response = await fetch(`${SUPABASE_URL}/rest/v1/rpc/service_request_update_status_v2`, {
          method: 'POST',
          headers: {
            'apikey': SUPABASE_ANON_KEY,
            'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            p_request_id: complaintId,
            p_new_status: newStatus,
            p_notes: notes || null,
            p_actor_id: actorId || null,
          }),
        });
        
        if (!v2Response.ok) {
          throw new Error('V2 function not available');
        }
        
        data = await v2Response.json();
        console.log('Using enhanced v2 status update function');
      } catch (v2Error) {
        console.log('V2 function not available, using original function');
        const originalResponse = await fetch(`${SUPABASE_URL}/rest/v1/rpc/service_request_update_status`, {
          method: 'POST',
          headers: {
            'apikey': SUPABASE_ANON_KEY,
            'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            p_request_id: complaintId,
            p_new_status: newStatus,
            p_notes: notes || null,
            p_actor_id: actorId || null,
          }),
        });
        
        if (!originalResponse.ok) {
          const errorData = await originalResponse.json().catch(() => ({ message: 'Request failed' }));
          throw new Error(errorData.message || 'Request failed');
        }
        
        data = await originalResponse.json();
      }

      // Handle different response formats (exactly like mobile app)
      let result;
      if (Array.isArray(data)) {
        result = data[0];
      } else if (typeof data === 'object' && data !== null) {
        result = data;
      } else {
        console.warn('Unexpected response format:', data);
        result = { success: true, message: 'Status updated' };
      }

      return {
        success: result?.success !== false,
        data: result,
        error: null,
        message: result?.message || 'Status updated successfully'
      };
    } catch (error) {
      console.error('Error updating complaint status:', error);
      return { success: false, error: error.message };
    }
  }

  // Update complaint pincode - EXACTLY like mobile app
  static async updateComplaintPincode(complaintId, pincode, advisorId = null) {
    try {
      console.log('Updating complaint pincode via RPC:', { complaintId, pincode, advisorId });
      
      const data = await supabaseRequest(`/rest/v1/rpc/update_complaint_pincode`, {
        method: 'POST',
        body: JSON.stringify({
          complaint_id: complaintId,
          new_pincode: pincode,
          advisor_id: advisorId || null
        }),
      });

      console.log('RPC result:', data);

      if (!data) {
        return {
          success: false,
          error: 'No response from server'
        };
      }

      // Parse the JSON response
      let result;
      if (typeof data === 'string') {
        try {
          result = JSON.parse(data);
        } catch (e) {
          console.error('Failed to parse JSON response:', e);
          return {
            success: false,
            error: 'Invalid response from server'
          };
        }
      } else {
        result = data;
      }
      
      console.log('Parsed result:', result);
      console.log('RPC success:', result.success);
      console.log('RPC message:', result.message);

      return {
        success: result.success || false,
        message: result.message || 'Unknown response',
        error: result.success ? null : (result.message || 'Unknown error')
      };
    } catch (error) {
      console.error('Error in updateComplaintPincode:', error);
      return {
        success: false,
        error: error.message || 'Failed to update pincode'
      };
    }
  }

  // Create new service request - EXACTLY like mobile app using service_executive_create_request RPC
  static async createServiceRequest(
    requestData,
    executiveId,
    executiveName
  ) {
    try {
      // Debug payload to verify media URLs are present
      try {
        console.log('[ServiceAdvisorService] creating request with media_upload count:', Array.isArray(requestData.media_upload) ? requestData.media_upload.length : 0);
        if (Array.isArray(requestData.media_upload)) {
          console.log('[ServiceAdvisorService] media_upload sample:', requestData.media_upload.slice(0, 3));
        }
      } catch {}
      
      // Map the data to match the exact database field names - EXACTLY like mobile app
      const data = await supabaseRequest(`/rest/v1/rpc/service_executive_create_request`, {
        method: 'POST',
        body: JSON.stringify({
          p_request_data: {
            customer_name: requestData.customer_name,
            vehicle_number: requestData.vehicle_number,
            service_type: requestData.service_type,
            priority: requestData.priority,
            contact_number: requestData.contact_number,
            alternate_number: requestData.alternate_number || '',
            problem: requestData.problem,
            address: requestData.address || '',
            pincode: requestData.pincode || '',
            customer_quoted_amount: requestData.customer_quoted_amount,
            advance_payment: requestData.advance_payment || '',
            service_tag: requestData.service_tag || '',
            location_link: requestData.location_link || '',
            media_upload: requestData.media_upload || [],
            source: requestData.source,
            remark: requestData.remark || '',
            is_premium: requestData.is_premium || false,
            card_number: requestData.card_number || '',
            registered_by_executive_id: executiveId,
            registered_by_executive_name: executiveName,
            request_status: 'pending',
            complaint_status: 'registered',
            drop_location: requestData.drop_location || ''
          }
        }),
      });

      if (data && data.length > 0) {
        const result = data[0];
        return {
          success: result.success,
          message: result.message,
          requestId: result.id
        };
      }

      return { success: false, message: 'Failed to create service request' };
    } catch (error) {
      console.error('Error creating service request:', error);
      return { success: false, message: error.message || 'Internal server error' };
    }
  }

  // Backward compatibility wrapper
  static async registerComplaint(complaintData) {
    // Convert old format to new format
    const requestData = {
      customer_name: complaintData.customerName,
      vehicle_number: complaintData.vehicleNumber || complaintData.vehicleDetails,
      service_type: complaintData.serviceType || 'other',
      priority: 'medium',
      contact_number: complaintData.phoneNumber,
      alternate_number: complaintData.alternateNumber || '',
      problem: complaintData.problemDescription || '',
      address: '',
      pincode: '',
      customer_quoted_amount: complaintData.quotedAmount ? parseFloat(complaintData.quotedAmount) : 0,
      advance_payment: complaintData.advancePayment || '',
      service_tag: complaintData.serviceTag || '',
      location_link: complaintData.locationLink || '',
      media_upload: complaintData.mediaUpload ? (Array.isArray(complaintData.mediaUpload) ? complaintData.mediaUpload : [complaintData.mediaUpload]) : [],
      source: complaintData.source || 'web',
      remark: '',
      is_premium: false,
      card_number: '',
      drop_location: complaintData.dropLocation || undefined,
    };

    return await this.createServiceRequest(
      requestData,
      complaintData.executiveId || '',
      complaintData.executiveName || ''
    );
  }

  // Send WhatsApp location request - EXACTLY like mobile app
  static async sendWhatsAppLocationRequest(phoneNumber) {
    try {
      const response = await fetch('https://public.doubletick.io/whatsapp/message/template', {
        method: 'POST',
        headers: {
          'accept': 'application/json',
          'content-type': 'application/json',
          'Authorization': 'key_ZUfIHHuFPQ'
        },
        body: JSON.stringify({
          messages: [{
            to: `91${phoneNumber}`,
            content: {
              templateName: "cust_location",
              language: "en",
              templateData: {
                body: {
                  placeholders: []
                }
              }
            }
          }]
        })
      });

      let result;
      try {
        result = await response.json();
      } catch (parseError) {
        console.error('Error parsing WhatsApp response:', parseError);
        result = { message: 'Invalid response from WhatsApp API' };
      }
      
      if (response.ok) {
        return {
          success: true,
          message: 'Location request sent successfully via WhatsApp',
        };
      } else {
        return {
          success: false,
          message: `Failed to send location request: ${result.message || 'Unknown error'}`,
        };
      }
    } catch (error) {
      console.error('WhatsApp location request error:', error);
      return {
        success: false,
        message: 'Failed to send location request. Please check your internet connection.',
      };
    }
  }

  // Update WhatsApp location sent status
  static async updateWhatsAppLocationSent(requestId) {
    try {
      // PATCH requests return empty response (204), so we don't parse JSON
      const response = await fetch(`${SUPABASE_URL}/rest/v1/service_requests?id=eq.${requestId}`, {
        method: 'PATCH',
        headers: {
          'apikey': SUPABASE_ANON_KEY,
          'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
          'Content-Type': 'application/json',
          'Prefer': 'return=minimal'
        },
        body: JSON.stringify({
          whatsapp_location_sent: true,
          whatsapp_location_sent_at: new Date().toISOString()
        }),
      });
      
      if (!response.ok) {
        const error = await response.json().catch(() => ({ message: 'Request failed' }));
        throw new Error(error.message || 'Request failed');
      }
      
      // PATCH returns 204 No Content, so no JSON to parse
      return { success: true, data: null };
    } catch (error) {
      console.error('Error updating WhatsApp location sent:', error);
      return { success: false, error: error.message };
    }
  }

  static async claimComplaint(requestId, advisorId, advisorName) {
    try {
      const data = await supabaseRequest(`/rest/v1/rpc/service_advisor_self_assign_complaint`, {
        method: 'POST',
        body: JSON.stringify({
          p_request_id: requestId,
          p_advisor_id: advisorId,
          p_advisor_name: advisorName || null,
        }),
      });
      return { success: true, data, error: null };
    } catch (error) {
      console.error('Error claiming complaint:', error);
      return { success: false, error: error.message };
    }
  }

  static async getAdvisorStatistics(advisorId) {
    try {
      const data = await supabaseRequest(`/rest/v1/rpc/service_advisor_get_statistics`, {
        method: 'POST',
        body: JSON.stringify({ p_advisor_id: advisorId }),
      });
      return { success: true, data: Array.isArray(data) ? data[0] : data, error: null };
    } catch (error) {
      console.error('Error fetching advisor statistics:', error);
      return { success: false, data: null, error: error.message };
    }
  }

  static async getAllAdvisors() {
    try {
      const data = await supabaseRequest(`/rest/v1/rpc/service_advisor_get_all_advisors`, {
        method: 'POST',
        body: JSON.stringify({}),
      });
      return { success: true, data: data || [], error: null };
    } catch (error) {
      console.error('Error fetching advisors:', error);
      return { success: false, data: [], error: error.message };
    }
  }

  static async assignComplaint(requestId, assignerId, targetAdvisorId, assignerName, targetAdvisorName) {
    try {
      const data = await supabaseRequest(`/rest/v1/rpc/service_advisor_assign_complaint`, {
        method: 'POST',
        body: JSON.stringify({
          p_request_id: requestId,
          p_assigner_id: assignerId,
          p_target_advisor_id: targetAdvisorId,
          p_assigner_name: assignerName || null,
          p_target_advisor_name: targetAdvisorName || null,
        }),
      });
      return { success: true, data, error: null };
    } catch (error) {
      console.error('Error assigning complaint:', error);
      return { success: false, error: error.message };
    }
  }

  static async unassignComplaint(requestId, advisorId) {
    try {
      const data = await supabaseRequest(`/rest/v1/rpc/service_advisor_unassign_complaint`, {
        method: 'POST',
        body: JSON.stringify({
          p_request_id: requestId,
          p_advisor_id: advisorId,
        }),
      });
      const result = Array.isArray(data) ? data[0] : data;
      return {
        success: result?.success !== false,
        message: result?.message || 'Complaint unassigned successfully',
        error: null
      };
    } catch (error) {
      console.error('Error unassigning complaint:', error);
      return { success: false, error: error.message };
    }
  }

  static async transferComplaintToAdvisor(requestId, fromAdvisorId, toAdvisorId, toAdvisorName) {
    try {
      const data = await supabaseRequest(`/rest/v1/rpc/service_advisor_transfer_complaint`, {
        method: 'POST',
        body: JSON.stringify({
          p_complaint_id: requestId,
          p_from_advisor_id: fromAdvisorId,
          p_to_advisor_id: toAdvisorId,
          p_to_advisor_name: toAdvisorName,
        }),
      });
      const result = Array.isArray(data) ? data[0] : data;
      return {
        success: result?.success !== false,
        message: result?.message || 'Complaint transferred successfully',
        error: null
      };
    } catch (error) {
      console.error('Error transferring complaint:', error);
      return { success: false, error: error.message };
    }
  }

  // Unassign mechanic from service request - EXACTLY like mobile app
  static async unassignMechanicFromRequest(requestId, unassignedById, unassignedByName = null) {
    try {
      const data = await supabaseRequest(`/rest/v1/rpc/unassign_mechanic_from_request`, {
        method: 'POST',
        body: JSON.stringify({
          p_request_id: requestId,
          p_unassigned_by_id: unassignedById,
          p_unassigned_by_name: unassignedByName || null,
        }),
      });
      const result = Array.isArray(data) ? data[0] : data;
      return {
        success: result?.success || false,
        message: result?.message || 'Mechanic unassigned successfully',
        error: null
      };
    } catch (error) {
      console.error('Error unassigning mechanic:', error);
      return { success: false, error: error.message };
    }
  }

  static async updateComplaintCompletion(requestId, completionData, updatedById, updatedByName = null) {
    try {
      const data = await supabaseRequest(`/rest/v1/rpc/update_complaint_completion_safe`, {
        method: 'POST',
        body: JSON.stringify({
          p_request_id: requestId,
          p_completion_notes: completionData.completionNotes || null,
          p_customer_quoted_amount: completionData.customerQuotedAmount || null,
          p_advance_payment: completionData.advancePayment || null,
          p_mechanic_payment: completionData.mechanicPayment || null,
          p_completion_media: completionData.completionMedia || null,
          p_updated_by_id: updatedById,
          p_updated_by_name: updatedByName || null,
        }),
      });
      const result = Array.isArray(data) ? data[0] : data;
      return { success: result?.success !== false, message: result?.message || 'Completion updated successfully' };
    } catch (error) {
      console.error('Error updating complaint completion:', error);
      return { success: false, message: error.message || 'Network error' };
    }
  }

  static async editCompletedComplaintWithinWindow(requestId, fields, actorId, actorName = null) {
    try {
      const data = await supabaseRequest(`/rest/v1/rpc/edit_completed_complaint_within_window`, {
        method: 'POST',
        body: JSON.stringify({
          p_request_id: requestId,
          p_customer_name: fields.customerName ?? null,
          p_contact_number: fields.contactNumber ?? null,
          p_pincode: fields.pincode ?? null,
          p_customer_quoted_amount: fields.customerQuotedAmount ?? null,
          p_advance_payment: fields.advancePayment ?? null,
          p_mechanic_payment: fields.mechanicPayment ?? null,
          p_actor_id: actorId,
          p_actor_name: actorName ?? null,
        }),
      });
      const result = Array.isArray(data) ? data[0] : data;
      return { success: !!result?.success, message: result?.message || 'Updated successfully' };
    } catch (error) {
      console.error('Error editing completed complaint:', error);
      return { success: false, message: error.message || 'Network error' };
    }
  }
}

